import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import { useRef, useEffect, useState, useMemo } from 'react';
import { useId } from '@react-aria/utils';
import { useControlledComponent } from "../../hooks/useControlledComponent";
import { useHover } from "./useHover";
import { isMobile } from "../../utils/is-mobile";
import { useTooltipContext } from "./TooltipContext";
export function useTooltip() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      isShowingProp = _ref.isShowing,
      onRequestChange = _ref.onRequestChange,
      _ref$defaultShowing = _ref.defaultShowing,
      defaultShowing = _ref$defaultShowing === void 0 ? false : _ref$defaultShowing,
      _ref$delayIn = _ref.delayIn,
      delayIn = _ref$delayIn === void 0 ? 100 : _ref$delayIn,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$enableOnMobile = _ref.enableOnMobile,
      enableOnMobile = _ref$enableOnMobile === void 0 ? false : _ref$enableOnMobile,
      idProp = _ref.id,
      anchorRef = _ref.anchorRef;

  var hasKeyboardFocusedTargetRef = useRef(false);
  var isDisabled = disabled;

  if (!enableOnMobile && isMobile) {
    isDisabled = true;
  } // use a unique id for accessibility purposes


  var id = useId(idProp);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      shouldUnmountWithoutAnimation = _useState2[0],
      setShouldUnmountWithoutAnimation = _useState2[1];

  var tooltipRef = useRef(null);
  var targetRef = useRef(null); // popover can be uncontrolled or controlled. This abstraction
  // handles both

  var _useControlledCompone = useControlledComponent(isShowingProp, onRequestChange, defaultShowing),
      _useControlledCompone2 = _slicedToArray(_useControlledCompone, 2),
      isShowing = _useControlledCompone2[0],
      setIsShowing = _useControlledCompone2[1];

  useEffect(function () {
    if (!isShowing) {
      hasKeyboardFocusedTargetRef.current = false;
    }
  }, [isShowing]);
  useEffect(function () {
    if (isDisabled && isShowing) {
      setIsShowing(false);
    }
  }, [isShowing, setIsShowing, isDisabled]);

  var _useTooltipContext = useTooltipContext(),
      onHideTooltip = _useTooltipContext.onHideTooltip,
      cancelUnmountAnimation = _useTooltipContext.cancelUnmountAnimation,
      shouldImmediatelyRenderTooltip = _useTooltipContext.shouldImmediatelyRenderTooltip,
      clearCancelUnmountFn = _useTooltipContext.clearCancelUnmountFn; // Whenever "isShowing" changes we need to determine if it
  // should mount immediately given our global tooltip state


  var shouldMountImmediately = useMemo(function () {
    if (isShowing) {
      return shouldImmediatelyRenderTooltip();
    }

    return false;
  }, [isShowing, shouldImmediatelyRenderTooltip]);

  var _useHover = useHover(isShowing, {
    delayIn: shouldMountImmediately || hasKeyboardFocusedTargetRef.current ? 0 : delayIn
  }),
      shouldMountTooltip = _useHover.shouldMountTooltip;

  useEffect(function () {
    if (shouldMountTooltip) {
      cancelUnmountAnimation();
      setShouldUnmountWithoutAnimation(false);
    }
  }, [cancelUnmountAnimation, shouldMountTooltip]);
  useEffect(function () {
    return function () {
      clearCancelUnmountFn(id);
    };
  }, [clearCancelUnmountFn, id]);
  /**
   * Target bindings (i.e., you pass this to a button.)
   */

  /**
   * When the mouse leaves the target element,
   * inform the tooltip global state manager. Provide
   * a callback which allows other tooltips to
   * potentially interrupt and immediately unmount
   * any tooltip which is animating out.
   */

  var onMouseLeave = function onMouseLeave() {
    function hideCallback() {
      setShouldUnmountWithoutAnimation(true);
    }

    if (shouldMountTooltip) {
      onHideTooltip(id, hideCallback);
    }

    setIsShowing(false);
  };

  var targetInteractionBindings = {
    'aria-describedby': isShowing ? id : undefined,
    onMouseEnter: function onMouseEnter() {
      return setIsShowing(true);
    },
    onMouseDown: function onMouseDown() {
      return setIsShowing(false);
    },
    onFocus: function onFocus(e) {
      if (e.target.hasAttribute('data-focus-visible-added')) {
        hasKeyboardFocusedTargetRef.current = true;
        setIsShowing(true);
      }
    },
    onBlur: function onBlur() {
      return setIsShowing(false);
    },
    onMouseLeave: onMouseLeave,
    onScrollCapture: shouldMountTooltip ? function () {
      return setIsShowing(false);
    } : undefined
  };

  var targetProps = _objectSpread({
    ref: targetRef
  }, !disabled && targetInteractionBindings);
  /**
   * Content bindings. (I.e., you pass this to the tooltip itself).
   */


  var contentProps = {
    id: id,
    ref: tooltipRef,
    target: anchorRef ? anchorRef.current : targetRef.current,
    role: 'tooltip',
    isShowing: shouldMountTooltip,
    animateOnMount: !shouldMountImmediately,
    animateOnUnmount: !shouldUnmountWithoutAnimation,
    shouldUsePositioner: true
  };
  return {
    isShowing: shouldMountTooltip,
    targetRef: targetRef,
    bindTarget: targetProps,
    bindContent: contentProps
  };
}